import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto animate-slide-up">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">Reshaping</span> the Testing Industry
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              We're not just another testing company. We're QA professionals who got tired of the status quo 
              and decided to build the future of testing automation ourselves.
            </p>
            <div className="flex justify-center">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="QA Team Collaboration" 
                className="rounded-lg shadow-2xl max-w-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center animate-on-scroll">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                A Group of QA in a <span className="gradient-text">One Man Show</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                It started with frustration. As seasoned QA professionals, we were tired of spending more time 
                setting up testing frameworks than actually testing. We were fed up with tools that promised 
                automation but delivered complexity.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                So we asked ourselves: What if testing could be as simple as it should be? What if we could 
                eliminate the grunt work and focus on what really matters—ensuring quality?
              </p>
              <p className="text-lg text-gray-600 mb-8">
                That's when EJBED was born. We combined decades of QA experience with modern technology to create 
                what we always wished existed—a testing platform that actually works the way QA professionals think.
              </p>
              <div className="flex items-center space-x-4">
                <div className="text-3xl font-bold text-primary-600">15+</div>
                <div className="text-gray-600">Years of Combined QA Experience</div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="QA Professional at Work" 
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="gradient-text">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              To reshape the development and testing business with faster, more accurate solutions 
              that empower teams to build better software.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 animate-on-scroll">
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Accelerate</h3>
              <p className="text-gray-600">
                Speed up development cycles by eliminating testing bottlenecks. 
                What used to take weeks now takes minutes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Automate</h3>
              <p className="text-gray-600">
                Remove manual processes and human error from testing. 
                Let intelligence handle the repetitive work.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dominate</h3>
              <p className="text-gray-600">
                Achieve excellence in quality assurance that sets your team apart 
                from the competition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Us <span className="gradient-text">Different</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlike tool vendors who've never run a real test suite in production, 
              we've lived the pain points that we're solving.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center animate-on-scroll">
            <div>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 9.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Built by QA, for QA</h3>
                    <p className="text-gray-600">
                      Every feature is designed by people who actually write and maintain test suites. 
                      We understand the daily challenges because we've lived them.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5-6l-5 5-5-5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-World Tested</h3>
                    <p className="text-gray-600">
                      Our platform isn't built in a lab—it's battle-tested in production environments 
                      with real APIs, real deadlines, and real complexity.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Obsessed with Speed</h3>
                    <p className="text-gray-600">
                      We believe that if testing slows you down, you're doing it wrong. 
                      Every decision we make prioritizes developer velocity.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Zero Compromise on Quality</h3>
                    <p className="text-gray-600">
                      Speed without accuracy is useless. Our platform finds the bugs that matter 
                      while filtering out the noise that wastes time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="Testing Excellence" 
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision for the Future */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="gradient-text">Vision</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We envision a world where testing is no longer a bottleneck but an accelerator for innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="Future of Testing" 
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Transforming How the World Tests Software
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Today, we're solving API testing. Tomorrow, we're tackling UI testing, performance testing, 
                and security testing. Our goal isn't just to build tools—it's to fundamentally change 
                how development teams think about quality assurance.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We believe that in the future, writing test cases by hand will seem as antiquated as 
                manually deploying servers. Testing will be intelligent, automatic, and seamlessly 
                integrated into every step of the development process.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <svg className="w-6 h-6 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Zero-configuration testing across all domains</span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg className="w-6 h-6 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">AI-powered test generation and maintenance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg className="w-6 h-6 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Predictive quality analytics and insights</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-900 text-white">
        <div className="container-max text-center animate-on-scroll">
          <h2 className="text-4xl font-bold mb-6">Ready to Be Part of the Revolution?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of QA professionals and developers who are already experiencing 
            the future of testing automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://ejbed.com/app/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Experience EJBED Today
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 